<template>
  <v-wait for="Comments List">
    <template slot="waiting">
      <div class="text-center pb-5">
        <b-spinner small=""></b-spinner>
      </div>
    </template>
    <div class="comments-list" v-if="comments.length || coupons || orderAddress">
      <template v-if="customerNote">
        <div class="single-comment single-comment--customer-note">
          <div class="user-avatar user-avatar--pin">
            <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="34" height="34" rx="17" fill="#FFA800" fill-opacity="0.15"/>
              <path d="M24.5114 14.3563L21.218 17.6638C21.5742 18.6536 21.7219 20.3114 20.1875 22.3513C20.0802 22.4947 19.9432 22.6133 19.7858 22.6989C19.6285 22.7845 19.4545 22.8352 19.2758 22.8474C19.2461 22.8474 19.2172 22.8474 19.1875 22.8474C19.0233 22.8474 18.8606 22.8151 18.7089 22.7522C18.5571 22.6893 18.4192 22.5972 18.3031 22.481L15.4167 19.5906L10.3175 24.6922C10.2003 24.8095 10.0412 24.8754 9.87535 24.8754C9.7095 24.8753 9.55044 24.8095 9.43316 24.6922C9.31588 24.5749 9.25 24.4159 9.25 24.25C9.25 24.0841 9.31588 23.9251 9.43316 23.8078L14.5347 18.7086L11.6451 15.8191C11.5211 15.6949 11.4246 15.546 11.3619 15.382C11.2993 15.2181 11.2718 15.0427 11.2814 14.8675C11.291 14.6922 11.3374 14.521 11.4175 14.3648C11.4977 14.2087 11.6098 14.0712 11.7467 13.9612C13.7326 12.3589 15.6334 12.6737 16.4654 12.9347L19.769 9.61622C19.8851 9.50011 20.0229 9.40801 20.1746 9.34518C20.3263 9.28234 20.4889 9.25 20.653 9.25C20.8172 9.25 20.9798 9.28234 21.1315 9.34518C21.2831 9.40801 21.4209 9.50011 21.537 9.61622L24.5107 12.5891C24.745 12.8234 24.8767 13.1411 24.8769 13.4725C24.877 13.8039 24.7456 14.1218 24.5114 14.3563Z" fill="#FF9D00"/>
            </svg>
          </div>
          <div class="comment-holder">
            <div class="comment-user">
              <b>Customer Note</b>
            </div>
            <div class="comment-time">
              {{ customerNote.created_at | moment('timezone', commentTimezone, 'Do MMM YYYY, ha') }}
            </div>
            <div class="comment-body comment-body--customer-note">
              <p>{{ getCustomerNoteContent(customerNote.content) }}</p>
            </div>
          </div>
        </div>
        <hr class="customer-note-divider" />
      </template>
      <div class="single-comment" v-for="(comment, index) in regularComments" v-bind:key="index">
        <template>
          <div class="user-avatar">
            <img v-if="comment.user.profile_image" alt="" :src="comment.user.profile_image" />
            <template v-else>{{ comment.user.initials | uppercase }}</template>
          </div>
          <div class="comment-holder">
            <div class="comment-user">
              <b v-if="comment.user.fullName">{{ comment.user.fullName }}</b>
              <span v-if="comment.user.username">@{{ comment.user.username }}</span>
            </div>
            <div class="comment-time">
              {{ comment.created_at | moment('timezone', commentTimezone, 'Do MMM YYYY, ha') }}
            </div>
            <div class="comment-body">
              <p>{{ sanitizeContent(comment.content) }}</p>
              <FileCard :files="comment.files"></FileCard>
            </div>
            <a
              class="action-button"
              href="javascript:;"
              v-if="currentStaffId === comment.user_id"
              @click="removeComment(comment.id)"
            >
              Remove
            </a>
            <a
              class="action-button"
              href="javascript:;"
              @click="updateComment(comment)"
              v-if="
                comment.orders && comment.orders.length > 0 && currentStaffId === comment.user_id
              "
            >
              Update
            </a>
          </div>
        </template>
      </div>
    </div>
    <div class="no-comments-alert" v-else>
      <figure>
        <img src="../assets/images/typing.svg" alt="" />
      </figure>
      <h4>No comments to speak of</h4>
      <p>Why don't you start the conversation?</p>
    </div>
  </v-wait>
</template>

<script>
import { mapMutations, mapState } from 'vuex';
import FileCard from './FileCard.vue';
import { stripHtml } from '@/support/sanitize';

export default {
  name: 'CommentsList',
  components: {
    FileCard,
  },
  data() {
    return {
      comments: [],
      orderAddress: null,
      coupons: [],
      hasCoupons: false,
    };
  },
  computed: {
    ...mapState({
      currentStaffId: state => state.auth.user.id,
      fetchCommentsCallback: state => state.commentModal.fetchCommentsCallback,
      fetchOrderCallback: state => state.commentModal.fetchOrderCallback,
      removeCommentCallback: state => state.commentModal.removeCommentCallback,
      commentTimezone: state => state.commentModal.timezone,
    }),
    customerNote() {
      return this.comments.find(c => this.isCustomerNote(c));
    },
    regularComments() {
      return this.comments.filter(c => !this.isCustomerNote(c));
    },
  },
  mounted() {
    this.fetchComments();
    this.$root.$on('commentsRefreshed', this.onCommentsRefreshed);
  },
  beforeDestroy() {
    this.$root.$off('commentsRefreshed', this.onCommentsRefreshed);
  },
  methods: {
    ...mapMutations('commentModal', {
      setSelectedUpdateNote: 'SET_SELECTED_UPDATE_NOTE',
    }),
    sanitizeContent(content) {
      return stripHtml(content);
    },
    isCustomerNote(comment) {
      return comment.content && comment.content.startsWith('Customer Note : ');
    },
    getCustomerNoteContent(content) {
      return stripHtml(content.replace('Customer Note : ', ''));
    },
    onCommentsRefreshed() {
      this.fetchComments();
    },
    updateComment(note) {
      this.setSelectedUpdateNote(note);
      this.$bvModal.show('comment-update-modal');
    },
    removeComment(noteId) {
      const h = this.$createElement;
      const message = h('span', {
        domProps: {
          innerHTML: 'Are you sure want to delete this comment? This action can not be undone.',
        },
      });
      this.$dialog('Delete Comment', [message]).then(value => {
        if (value) {
          this.removeCommentCallback(noteId)
            .then(() => {
              this.$store.dispatch('showSuccessAlert', 'Deleted Successfully');
              this.fetchComments();
              this.$root.$emit('commentsRefreshed');
            })
            .catch(({ response }) => {
              this.$store.dispatch('showErrorAlert', this._.get(response, 'data.error', 'Error'));
            });
        }
      });
    },
    fetchComments() {
      this.$wait.start('Comments List');
      this.fetchCommentsCallback()
        .then(response => {
          const comments = this._.get(response, 'data.data', []);
          this.comments = comments
            .map(comment => {
              if (!comment.user) {
                const systemUser = {
                  first_name: 'System',
                  last_name: '',
                  username: 'system',
                };
                /* eslint-disable */
                comment.user = systemUser;
              }
              comment.user.fullName = this.getFullName(
                comment.user.first_name,
                comment.user.last_name,
              );
              comment.user.initials = comment.user.fullName
                ? this.createLetterAvatar(comment.user.first_name, comment.user.last_name)
                : comment.user.username.slice(0, 2);
              /* eslint-enable */

              return comment;
            })
            .reverse();
        })
        .catch(response => {
          this.$store.dispatch('showErrorAlert', this._.get(response, 'data.error', 'Error'));
        })
        .finally(() => {
          this.$wait.end('Comments List');
        });
      if (this.fetchOrderCallback) {
        this.fetchOrderCallback().then(response => {
          this.$wait.start('Comments List');
          if (response.data.data.order_billing) {
            this.orderAddress = this._.get(
              response,
              'data.data.order_billing.billing_address.address',
            );
          }
          this.coupons = this._.get(response, 'data.data.coupons.data');
          if (this.coupons) {
            this.hasCoupons = true;
          }
          this.$wait.end('Comments List');
        });
      }
    },
  },
};
</script>
